/**
 * MVP rule catalogue — the SINGLE SOURCE OF TRUTH for rules (docs/CONTRACT.md §3).
 *
 * The DB table `compliance_rules` is future-facing and NOT used by the MVP engine.
 * Each entry here is metadata (severity/message/reference); the actual predicate
 * lives in `engine.ts` keyed by `rule_id` (JSONLogic is intentionally NOT evaluated
 * in the MVP — predicates are typed TS functions for determinism & testability).
 *
 * Severity discipline (DP-4 / DP-7):
 *  - BLOCK : deterministic from deal data AND within exporter (BRITZMEDI) control.
 *  - ERROR : must be acknowledged before issue.
 *  - WARN  : advisory.
 *  - INFO  : informational / checklist hand-off to the US importer.
 *
 * NO tariff rate anywhere (DP-3). No US-entry-identifier BLOCKs (DP-7, B-2):
 * entry identifiers (FEI / product code / 510k / intended_use) are WARN/checklist.
 */

import type { ComplianceRule, RuleCategory, RuleSeverity } from "@/types";

/** Catalogue entry id type alias (kept loose: rule_id is a string PK). */
export type RuleId = string;

/**
 * Catalogue record. Mirrors `ComplianceRule` but `condition` is left empty `{}`
 * in the MVP (the predicate is a TS function in `engine.ts`, see PREDICATES).
 */
export type CatalogRule = ComplianceRule;

function rule(
  rule_id: RuleId,
  category: RuleCategory,
  severity: RuleSeverity,
  name: string,
  message: string,
  reference: string | null,
  run_at: ComplianceRule["run_at"],
): CatalogRule {
  return {
    rule_id,
    name,
    category,
    scope: "deal",
    condition: {}, // MVP: predicate is a typed function in engine.ts, not JSONLogic.
    severity,
    message,
    fix_suggestion: null,
    reference,
    run_at,
    mvp_enabled: true,
    enabled: true,
  };
}

const SAVE_AND_ISSUE: ComplianceRule["run_at"] = [
  "on_deal_save",
  "before_document_issue",
];
const ISSUE_ONLY: ComplianceRule["run_at"] = ["before_document_issue"];

/**
 * MVP rule catalogue (~14 rules). IDs match docs/CONTRACT.md §3.
 * Order here is the display order in findings lists.
 */
export const MVP_RULES: CatalogRule[] = [
  // --- cross-field drift (payload vs current master / CI↔PL consistency) ------
  rule(
    "CF_LINE_AMOUNT",
    "cross_field",
    "ERROR",
    "라인 금액 정합",
    "라인 금액이 수량 × 단가와 일치하지 않습니다.",
    null,
    SAVE_AND_ISSUE,
  ),
  rule(
    "CF_QTY_PACKAGE",
    "cross_field",
    "WARN",
    "포장 단위 합 정합",
    "포장 수량 합계가 총 수량과 일치하는지 확인하세요.",
    null,
    SAVE_AND_ISSUE,
  ),

  // --- amount / weight / unit -------------------------------------------------
  rule(
    "AW_NET_GT_GROSS",
    "amount_weight",
    "ERROR",
    "순중량 > 총중량",
    "순중량이 총중량보다 큽니다. 중량 입력을 확인하세요.",
    null,
    SAVE_AND_ISSUE,
  ),
  rule(
    "AW_WEIGHT_UNIT_MIX",
    "amount_weight",
    "ERROR",
    "중량 단위(kg/lb) 혼용",
    "라인 간 중량 단위(kg/lb)가 혼용되었습니다. 미국 거래 빈발 오류입니다.",
    null,
    SAVE_AND_ISSUE,
  ),
  rule(
    "AW_CURRENCY_MISSING",
    "amount_weight",
    "ERROR",
    "통화 누락",
    "거래 통화가 지정되지 않았습니다.",
    null,
    SAVE_AND_ISSUE,
  ),

  // --- required document set --------------------------------------------------
  rule(
    "DM_REQUIRED_SET",
    "doc_missing",
    "WARN",
    "필수 서류 세트",
    "수입국 프로파일이 요구하는 서류가 아직 생성되지 않았습니다.",
    null,
    ISSUE_ONLY,
  ),

  // --- incoterm ---------------------------------------------------------------
  rule(
    "IC_KOREAN_FOB_AIR",
    "incoterm",
    "WARN",
    "FOB + 항공/특송",
    "FOB는 인코텀즈 2020상 해상·내수로 전용입니다. 항공/특송은 FCA가 적합합니다.",
    "Incoterms 2020 (ICC)",
    SAVE_AND_ISSUE,
  ),
  rule(
    "IC_NAMED_PLACE_VAGUE",
    "incoterm",
    "WARN",
    "인코텀즈 지정장소 누락",
    "인코텀즈 뒤 지정장소가 비어 있습니다(예: 'FCA ICN').",
    "Incoterms 2020 (ICC)",
    SAVE_AND_ISSUE,
  ),
  rule(
    "IC_VERSION_MISSING",
    "incoterm",
    "INFO",
    "인코텀즈 버전 미표기",
    "인보이스에 'Incoterms® 2020' 버전 표기를 권장합니다.",
    "Incoterms 2020 (ICC)",
    SAVE_AND_ISSUE,
  ),
  rule(
    "IC_MODE_MISMATCH",
    "incoterm",
    "WARN",
    "인코텀즈-운송수단 불일치",
    "선택한 인코텀즈가 해상 전용인데 운송수단이 해상이 아닙니다.",
    "Incoterms 2020 (ICC)",
    SAVE_AND_ISSUE,
  ),
  rule(
    "IC_DDP_FOREIGN_IOR",
    "incoterm",
    "WARN",
    "DDP 외국기업 고위험",
    "DDP는 BRITZMEDI가 미국 IOR·수입통관·관세를 부담합니다 — 외국기업에 고위험(비권장).",
    "Incoterms 2020 (ICC)",
    SAVE_AND_ISSUE,
  ),

  // --- importer gate (generalised, v2) ----------------------------------------
  // REQ_IMPORTER_ROLE replaces the US-only ERR_NO_IOR / ERR_NO_INITIAL_IMPORTER
  // BLOCKs (OD-v2-3): countries.required_importer_roles drives it as DATA.
  // US seed = {importer_of_record, fda_initial_importer} -> behaviour identical.
  rule(
    "REQ_IMPORTER_ROLE",
    "country_regulatory",
    "BLOCK",
    "목적국 필수 수입자 역할 미지정",
    "목적국이 요구하는 필수 수입자 역할이 딜에 지정되지 않았습니다. (미국: IOR·FDA Initial Importer / 인도: 공인대리인 / 대만: LAR)",
    "countries.required_importer_roles",
    SAVE_AND_ISSUE,
  ),
  rule(
    "ERR_USAGENT_NOT_IMPORTER",
    "us_regulatory",
    "WARN",
    "US Agent ≠ Initial Importer",
    "US Agent 지정만으로 FDA Initial Importer 요건이 자동 충족되지 않습니다(별도 등록·미국주소·연회비 확인 필요).",
    "21 CFR 807.40",
    SAVE_AND_ISSUE,
  ),
  rule(
    "ERR_DIRECT_CLINIC_IOR",
    "us_regulatory",
    "WARN",
    "직판인데 IOR=클리닉 안티패턴",
    "직판 구조에서 클리닉을 IOR로 지정하는 것은 안티패턴입니다. 디스트리뷰터/3PL/미국법인 구조를 검토하세요.",
    null,
    SAVE_AND_ISSUE,
  ),
  rule(
    "ERR_ENTRY_IDS_MISSING",
    "us_regulatory",
    "WARN", // DP-7 / B-2: never a BLOCK — US-importer hand-off checklist.
    "Entry 규제식별자 전달 항목",
    "미국 수입자에게 전달할 entry 식별자(FEI/product code/510k)가 일부 비어 있습니다. 통관사 확인 항목입니다.",
    null,
    ISSUE_ONLY,
  ),
  rule(
    "ERR_LISTING_INACTIVE",
    "us_regulatory",
    "ERROR",
    "FDA Device Listing 비활성",
    "제품의 FDA Device Listing이 활성(active) 상태가 아닙니다.",
    "21 CFR 807.25",
    SAVE_AND_ISSUE,
  ),

  // --- tariff guard (data-evaluable ack only; no rate detection, D-2) ---------
  rule(
    "TG_TARIFF_ACK",
    "tariff_guard",
    "ERROR",
    "관세 통관사 확인 미확인",
    "'세율은 선적시점 통관사/CBP ACE에서 확인'에 대한 사용자 확인이 필요합니다.",
    null,
    ISSUE_ONLY,
  ),

  // --- payment ----------------------------------------------------------------
  rule(
    "PAY_BEFORE_PACK",
    "payment",
    "WARN",
    "선입금 미확인",
    "결제조건이 선입금인데 입금 확인이 아직 안 되었습니다.",
    null,
    SAVE_AND_ISSUE,
  ),

  // --- HS ---------------------------------------------------------------------
  rule(
    "EXP_HS_UNVERIFIED",
    "doc_missing",
    "INFO",
    "HS코드 미확인",
    "HS코드가 통관사 확인(hs_verified) 전 추정값일 수 있습니다.",
    null,
    SAVE_AND_ISSUE,
  ),

  // ==== v2 (export-workflow-v2) — research basis: docs/00-research/ ============
  // --- generic format / presence (trade-doc-validation-rules.md §5) -----------
  rule(
    "GEN_LINES_PRESENT",
    "cross_field",
    "ERROR",
    "라인아이템 없음",
    "발행할 제품 라인아이템이 없습니다.",
    null,
    ISSUE_ONLY,
  ),
  rule(
    "GEN_CURRENCY_ISO4217",
    "format",
    "ERROR",
    "통화 코드 형식",
    "거래 통화가 ISO 4217 3자리 코드가 아닙니다(예: USD, EUR, INR, TWD).",
    "ISO 4217",
    SAVE_AND_ISSUE,
  ),
  rule(
    "GEN_INCOTERM_ENUM",
    "format",
    "ERROR",
    "인코텀즈 코드 무효",
    "인코텀즈가 Incoterms 2020의 11개 규칙에 없습니다.",
    "Incoterms 2020 (ICC)",
    SAVE_AND_ISSUE,
  ),
  rule(
    "GEN_HS_FORMAT",
    "format",
    "WARN",
    "HS코드 자릿수",
    "HS코드가 국제 최소 6자리에 미달합니다.",
    "WCO HS",
    SAVE_AND_ISSUE,
  ),
  rule(
    "GEN_ORIGIN_PRESENT",
    "doc_missing",
    "ERROR",
    "원산지 미기재",
    "제품의 원산지(country of origin)가 비어 있습니다. CI 필수 기재사항입니다.",
    "19 CFR 142.6 / trade.gov",
    ISSUE_ONLY,
  ),
  rule(
    "GEN_SIGNATURE_READY",
    "doc_missing",
    "WARN",
    "서명 이미지 미등록",
    "자사 서명/도장 이미지가 등록되지 않아 발행물에 서명이 삽입되지 않습니다.",
    null,
    ISSUE_ONLY,
  ),
  rule(
    "GEN_PKG_COUNT",
    "amount_weight",
    "WARN",
    "포장 수 미기재",
    "포장 수(number of packages)가 기재되지 않았습니다. PL 필수 항목입니다.",
    null,
    ISSUE_ONLY,
  ),
  rule(
    "GEN_INSURANCE_REQUIRED",
    "incoterm",
    "ERROR",
    "적하보험 증권 미기록",
    "CIF/CIP는 매도인 부보 의무입니다. 보험증권 번호를 기록하세요(부보 수준·금액은 계약/신용장 조건과 인코텀즈 가이드 참조).",
    "Incoterms 2020 (ICC) / UCP600 Art.28(f)",
    ISSUE_ONLY,
  ),

  // --- India (india-export-requirements.md) -----------------------------------
  rule(
    "IN_AGENT_MD15",
    "country_regulatory",
    "WARN",
    "인도 MD-15 미확인",
    "인도 공인대리인의 CDSCO MD-15 수입 라이선스 번호가 확인·기록되지 않았습니다. MD-15 없이는 통관 불가(형사처벌 대상)입니다.",
    "CDSCO MDR 2017 / SUGAM",
    SAVE_AND_ISSUE,
  ),
  rule(
    "IN_IMPORTER_IDS",
    "country_regulatory",
    "WARN",
    "인도 수입자 식별자 미수집",
    "인도 수입자의 IEC 또는 GSTIN이 기록되지 않았습니다(Bill of Entry 필수).",
    "ICEGATE / CBIC",
    SAVE_AND_ISSUE,
  ),
  rule(
    "IN_HSN8",
    "country_regulatory",
    "WARN",
    "인도 HSN 8자리 미달",
    "인도향 라인의 HS코드가 8자리(HSN) 미만입니다. 인도 통관은 8자리 분류를 사용하며 한-인도 세번 상이는 조사 리스크입니다.",
    "India FTP / CAROTAR 2020",
    SAVE_AND_ISSUE,
  ),
  rule(
    "IN_CEPA_CO_MISSING",
    "country_regulatory",
    "WARN",
    "CEPA CO 워크시트 미생성",
    "CEPA 특혜 청구 의도인데 CO 신청 워크시트가 생성되지 않았습니다. 한-인도 CEPA CO는 기관발급만 인정됩니다(UNI-PASS/대한상의).",
    "한-인도 CEPA",
    ISSUE_ONLY,
  ),
  rule(
    "IN_CO_STALE",
    "country_regulatory",
    "WARN",
    "CEPA CO 워크시트 구버전",
    "CEPA CO 워크시트 발행 이후 딜 데이터가 변경되었습니다. CO-CI 정보 불일치는 특혜 부인 사유입니다 — 워크시트를 재발행하세요.",
    "CAROTAR 2020",
    ISSUE_ONLY,
  ),

  // --- Taiwan (taiwan-export-requirements.md) ---------------------------------
  rule(
    "TW_LAR_TFDA",
    "country_regulatory",
    "WARN",
    "대만 TFDA 허가 미확인",
    "대만 LAR의 TFDA 제품허가증(査驗登記) 번호가 확인·기록되지 않았습니다. 허가 없이는 상업 목적 통관이 불가합니다.",
    "대만 의료기기관리법(2021)",
    SAVE_AND_ISSUE,
  ),
  rule(
    "TW_CI_IMPORT_LICENSE",
    "country_regulatory",
    "WARN",
    "대만 수입허가번호 미기록",
    "대만 CI에 기재할 수입허가번호(import license no.)가 기록되지 않았습니다. CI 품명·금액은 수입허가와 일치해야 합니다.",
    "trade.gov Taiwan CCG",
    ISSUE_ONLY,
  ),
  rule(
    "TW_CN_LABEL",
    "country_regulatory",
    "INFO",
    "대만 중문 라벨 확인",
    "정체(Traditional) 중문 라벨+IFU 의무: 허가번호·LAR 기재, 중문 제품명을 외국어명보다 작게 표기 불가. 체크리스트를 확인하세요.",
    "TFDA 라벨 규정",
    ISSUE_ONLY,
  ),
  rule(
    "TW_NO_FTA_CO",
    "country_regulatory",
    "INFO",
    "대만 특혜 청구 불가",
    "한-대만 FTA가 없어 특혜관세 청구가 불가합니다. 필요 시 KCCI 일반(비특혜) CO만 발급 가능합니다.",
    "fta.go.kr",
    SAVE_AND_ISSUE,
  ),

  // --- L/C minimal (OD-v2-1; UCP600 basics only, full doc-exam is out of scope)
  rule(
    "LC_EXPIRY",
    "payment",
    "ERROR",
    "L/C 유효기일 경과",
    "L/C 유효기일(expiry)이 지났습니다. 기일 경과 제시는 지급 거절 사유입니다.",
    "UCP600 Art.6",
    SAVE_AND_ISSUE,
  ),
  rule(
    "LC_LATE_SHIPMENT",
    "payment",
    "ERROR",
    "L/C 최종선적일 경과",
    "선적 전인데 L/C 최종선적일(latest shipment date)이 지났습니다. Late shipment는 대표적 하자 사유입니다.",
    "UCP600",
    SAVE_AND_ISSUE,
  ),
];

/** Lookup map: rule_id -> catalogue metadata. */
export const RULE_BY_ID: Record<RuleId, CatalogRule> = Object.fromEntries(
  MVP_RULES.map((r) => [r.rule_id, r]),
);
